<div class="flex flex-col gap-6">
  <div class="flex justify-between items-center bg-gray-2 p-4 rounded-xl border border-gray-4 shadow-sm">
    <h2 class="text-xl font-semibold text-gray-12">Gestion des photos</h2>
    <div class="flex gap-3 items-center">
      <button mat-flat-button class="primary" (click)="doneClicked.emit()">Terminé</button>
      <button mat-mini-fab class="primary" (click)="addClicked.emit()" aria-label="Ajouter une photo">
        <mat-icon>add</mat-icon>
      </button>
    </div>
  </div>

  <!-- Explanatory Privacy Banner -->
  <div
    class="flex items-start gap-3.5 p-4 rounded-xl bg-brand-2/70 border border-brand-5/80 text-brand-12 text-sm leading-relaxed shadow-xs"
  >
    <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-4 text-brand-11 mt-0.5">
      <mat-icon class="!w-4 !h-4 !text-[18px]">shield</mat-icon>
    </div>
    <div class="flex flex-col gap-1">
      <span class="font-semibold text-gray-12 text-sm">Protection et confidentialité des photos</span>
      <p class="text-sm text-gray-11 m-0 leading-normal">
        Pour protéger l'adresse et préserver l'exclusivité de votre bien, les photos de
        <strong>façade, vues, extérieur, parties communes et stationnement</strong> sont automatiquement
        masquées aux agents immobiliers lors de leur exploration. Elles ne seront visibles qu'une fois la
        première visite effectuée.
      </p>
      <span class="text-[11px] text-brand-11 font-medium mt-0.5 flex items-center gap-1">
        <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">info</mat-icon>
        La photo de couverture doit rester visible à tous (pièce intérieure ou sans catégorie restreinte).
      </span>
    </div>
  </div>

  <div class="flex flex-col gap-4" cdkDropList (cdkDropListDropped)="handleDrop($event)">
    @for (photo of photos(); track photo.file || $index; let i = $index) {
      <div
        class="flex items-center gap-6 p-4 surface-default border border-gray-4 rounded-xl shadow-sm hover:shadow-md transition-shadow"
        cdkDrag
      >
        <!-- Drag Handle -->
        <div
          class="text-gray-6 flex flex-col items-center cursor-grab hover:text-gray-12 transition-colors"
          cdkDragHandle
        >
          <mat-icon class="!w-6 !h-6 !text-[24px]">drag_indicator</mat-icon>
        </div>

        <!-- Thumbnail -->
        <div class="w-32 h-32 rounded-lg overflow-hidden shrink-0 relative border border-gray-4 shadow-inner">
          <img [src]="photo.url" class="w-full h-full object-cover" alt="Property photo" />
          @if (i === 0) {
            <div
              class="absolute top-0 left-0 right-0 bg-brand-9/90 text-white text-[10px] font-bold uppercase py-1 text-center backdrop-blur-sm shadow-sm"
            >
              Couverture
            </div>
          }
        </div>

        <!-- Controls -->
        <div class="flex flex-col gap-4 flex-1">
          <div class="flex justify-between items-start">
            <div class="min-w-[240px]">
              <mat-form-field appearance="outline" class="w-full !pb-0" subscriptSizing="dynamic">
                <mat-label>Associer à une pièce / espace</mat-label>
                <mat-select
                  [ngModel]="getRefValue(photo)"
                  (ngModelChange)="onCharacteristicChange(photo, $event)"
                >
                  <mat-option [value]="''">Aucune sélection</mat-option>

                  @if (availableCharacteristics.interior.length > 0) {
                    <mat-optgroup label="Intérieur">
                      @for (char of availableCharacteristics.interior; track char.value) {
                        <mat-option [value]="char.value">
                          {{ char.label }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }

                  @if (availableCharacteristics.general.length > 0) {
                    <mat-optgroup
                      [disabled]="i === 0"
                      [label]="i === 0 ? 'Général & Vues (Indisponible en couverture)' : 'Général & Vues'"
                    >
                      @for (char of availableCharacteristics.general; track char.value) {
                        <mat-option [value]="char.value" [disabled]="i === 0">
                          {{ char.label }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }

                  @if (availableCharacteristics.exterior.length > 0) {
                    <mat-optgroup
                      [disabled]="i === 0"
                      [label]="i === 0 ? 'Extérieur (Indisponible en couverture)' : 'Extérieur'"
                    >
                      @for (char of availableCharacteristics.exterior; track char.value) {
                        <mat-option [value]="char.value" [disabled]="i === 0">
                          {{ char.label }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }

                  @if (availableCharacteristics.commonAreas.length > 0) {
                    <mat-optgroup
                      [disabled]="i === 0"
                      [label]="i === 0 ? 'Espaces communs (Indisponible en couverture)' : 'Espaces communs'"
                    >
                      @for (char of availableCharacteristics.commonAreas; track char.value) {
                        <mat-option [value]="char.value" [disabled]="i === 0">
                          {{ char.label }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }

                  @if (availableCharacteristics.parkings.length > 0) {
                    <mat-optgroup
                      [disabled]="i === 0"
                      [label]="i === 0 ? 'Stationnement (Indisponible en couverture)' : 'Stationnement'"
                    >
                      @for (char of availableCharacteristics.parkings; track char.value) {
                        <mat-option [value]="char.value" [disabled]="i === 0">
                          {{ char.label }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }
                </mat-select>
              </mat-form-field>

              <!-- Visibility & Security Tagging -->
              <div class="flex flex-wrap items-center gap-3 mt-3">
                @if (isAdmin()) {
                  @if (i === 0) {
                    <span class="text-xs text-gray-10 italic"
                      >Photo de couverture (doit rester visible à tous)</span
                    >
                  } @else {
                    <mat-slide-toggle
                      [checked]="photo.isHiddenBeforeMandate || false"
                      (change)="toggleHideBeforeMandate(photo)"
                      class="text-xs"
                    >
                      <span class="text-xs text-gray-12 font-medium">Masquer avant la première visite</span>
                    </mat-slide-toggle>
                  }
                }

                @if (isMandateOnly(photo)) {
                  <div
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-3 text-amber-11 border border-amber-6"
                    matTooltip="Cette photo ne sera accessible aux agents qu'une fois la première visite effectuée"
                  >
                    <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">lock</mat-icon>
                    @if (photo.isHiddenBeforeMandate) {
                      <span>Masqué par l'admin (première visite requise)</span>
                    } @else {
                      <span>Visible après la 1ère visite</span>
                    }
                  </div>
                } @else {
                  <div
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-jade-3 text-jade-11 border border-jade-6"
                    matTooltip="Photo visible par tous les agents lors de l'exploration"
                  >
                    <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">visibility</mat-icon>
                    <span>Visible par tous les agents</span>
                  </div>
                }
              </div>
            </div>

            <!-- Reorder buttons -->
            <div class="flex flex-col gap-1">
              <button
                mat-icon-button
                [disabled]="i === 0 || (i === 1 && isMandateOnly(photo))"
                (click)="moveUp(i)"
                [title]="
                  i === 1 && isMandateOnly(photo)
                    ? 'Une photo visible uniquement après la première visite ne peut pas être définie en couverture'
                    : 'Déplacer vers le haut'
                "
              >
                <mat-icon>expand_less</mat-icon>
              </button>
              <button
                mat-icon-button
                [disabled]="i === photos().length - 1"
                (click)="moveDown(i)"
                title="Déplacer vers le bas"
              >
                <mat-icon>expand_more</mat-icon>
              </button>
            </div>
          </div>

          <div class="flex items-center gap-4 mt-auto">
            @if (i === 0) {
              <div
                class="flex items-center gap-2 px-3 py-1.5 bg-brand-2 text-brand-11 rounded-full border border-brand-4 shadow-sm"
              >
                <mat-icon class="!w-4 !h-4 !text-[16px] text-brand-9">star</mat-icon>
                <span class="text-[11px] font-bold uppercase tracking-wider"
                  >Photo de couverture (Visible par tous)</span
                >
              </div>
            } @else {
              <div class="flex items-center gap-2 text-gray-9">
                <mat-icon class="!w-4 !h-4 !text-[16px] opacity-40">info_outline</mat-icon>
                <span class="text-xs italic">Placez cette photo en première position pour la couverture</span>
              </div>

              <button mat-button color="warn" class="ml-auto !px-3" (click)="removePhoto(photo)">
                <mat-icon>delete_outline</mat-icon>
                Supprimer
              </button>
            }
          </div>
        </div>
      </div>
    } @empty {
      <div class="p-12 text-center text-gray-11 bg-gray-2 rounded-xl border border-dashed border-gray-4">
        <mat-icon class="!w-12 !h-12 !text-[48px] mb-2 opacity-20">no_photography</mat-icon>
        <p>Aucune photo à gérer.</p>
      </div>
    }
  </div>
</div>
